import { Printer, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const longDate = (s) => (s ? new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "—");
const addrLines = (a) => {
  if (!a) return [];
  const city = [a.city, a.state].filter(Boolean).join(", ");
  return [a.line1, a.line2, [city, a.postal_code].filter(Boolean).join(" ")].filter(Boolean);
};
const TAX_RESOLVED = ["CALCULATED", "OVERRIDDEN", "EXEMPT"];

export function ProposalToolbar({ pdfUrl, extra }) {
  return (
    <div className="print:hidden mb-4 flex flex-wrap items-center justify-end gap-2" data-testid="proposal-doc-toolbar">
      {extra}
      <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="proposal-doc-print" className="border-limestone text-charcoal hover:bg-sand">
        <Printer className="mr-1 h-3.5 w-3.5" /> Print
      </Button>
      {pdfUrl && (
        <Button size="sm" onClick={() => window.open(pdfUrl, "_blank", "noopener")} data-testid="proposal-doc-pdf" className="bg-terracotta text-white hover:bg-terracotta-hover">
          <FileDown className="mr-1 h-3.5 w-3.5" /> PDF
        </Button>
      )}
    </div>
  );
}

export function ProposalDocument({ data, logoUrl }) {
  const b = data.branding || {};
  const brand = b.display_name || data.dealer_name || "";
  const pf = data.prepared_for || {};
  const tax = data.tax;
  const taxResolved = tax && TAX_RESOLVED.includes(tax.status);
  return (
    <article className="rounded-md border border-border bg-white p-6 shadow-[0_2px_12px_rgba(26,26,24,0.04)] print:border-0 print:p-0 print:shadow-none sm:p-10" data-testid="proposal-document">
      <header className="flex flex-wrap items-start justify-between gap-6 border-b border-border pb-6">
        <div className="min-w-0">
          {logoUrl && b.has_logo ? (
            <img src={logoUrl} alt={brand} className="mb-2 max-h-14 w-auto object-contain" data-testid="proposal-doc-logo" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <h1 className="font-display text-3xl font-medium tracking-tight text-charcoal" data-testid="proposal-doc-brand">{brand}</h1>
          )}
          <div className="mt-1 text-xs text-warmgrey">
            {addrLines(b.public_address).map((l) => <p key={l}>{l}</p>)}
            <p>{[b.cs_phone, b.cs_email].filter(Boolean).join(" · ")}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-display text-2xl font-medium tracking-tight text-charcoal">Proposal</p>
          <p className="mt-1 font-mono text-xs text-warmgrey" data-testid="proposal-doc-number">No. {data.proposal_number}</p>
          <p className="text-xs text-warmgrey">Issue date: {longDate(data.issued_at || data.created_at)}</p>
          <p className="mt-2 text-sm font-semibold text-[#B86F54]" data-testid="proposal-doc-valid-until">Valid until {longDate(data.expires_at)}</p>
        </div>
      </header>

      <section className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2">
        <div data-testid="proposal-doc-prepared-for">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B86F54]">Prepared for</p>
          <p className="mt-2 text-sm font-medium text-charcoal">{pf.name || <span className="text-warmgrey">—</span>}</p>
          {pf.attention && <p className="text-sm text-charcoal">Attn: {pf.attention}</p>}
          <div className="text-sm text-warmgrey">{addrLines(pf.billing_address).map((l) => <p key={l}>{l}</p>)}</div>
          {(pf.phone || pf.email) && <p className="text-sm text-warmgrey">{[pf.phone, pf.email].filter(Boolean).join(" · ")}</p>}
        </div>
        <div data-testid="proposal-doc-project">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B86F54]">Project</p>
          <p className="mt-2 text-sm font-medium text-charcoal">{data.project_name}</p>
          {data.delivery_address && (
            <div className="text-sm text-warmgrey">
              <p className="text-xs uppercase tracking-wider">Delivery</p>
              {addrLines(data.delivery_address).map((l) => <p key={l}>{l}</p>)}
            </div>
          )}
        </div>
      </section>

      <table className="w-full text-sm" data-testid="proposal-doc-lines">
        <thead>
          <tr className="bg-sand text-[11px] uppercase tracking-wider text-charcoal">
            <th className="px-2 py-2 text-left font-semibold">Product</th>
            <th className="px-2 py-2 text-right font-semibold">Boxes</th>
            <th className="hidden px-2 py-2 text-right font-semibold sm:table-cell">Coverage</th>
            <th className="hidden px-2 py-2 text-right font-semibold sm:table-cell">$/sq ft</th>
            <th className="px-2 py-2 text-right font-semibold">$/box</th>
            <th className="px-2 py-2 text-right font-semibold">Total</th>
          </tr>
        </thead>
        <tbody>
          {data.lines.map((l, i) => (
            <tr key={i} className="border-b border-border" data-testid={`proposal-doc-line-${i}`}>
              <td className="px-2 py-2.5 text-charcoal">{l.dealer_label || l.variant_label}</td>
              <td className="num px-2 py-2.5 text-right">{l.boxes}</td>
              <td className="num hidden px-2 py-2.5 text-right sm:table-cell">{l.coverage_sqft ? `${l.coverage_sqft} sq ft` : "—"}</td>
              <td className="num hidden px-2 py-2.5 text-right sm:table-cell">{money(l.customer_price_per_sqft)}</td>
              <td className="num px-2 py-2.5 text-right">{money(l.customer_price_per_box)}</td>
              <td className="num px-2 py-2.5 text-right text-charcoal">{money(l.line_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <section className="ml-auto mt-4 w-full max-w-xs space-y-1.5 text-sm" data-testid="proposal-doc-totals">
        <div className="flex justify-between"><span className="text-warmgrey">Subtotal</span><span className="num text-charcoal">{money(data.subtotal_usd)}</span></div>
        <div className="flex justify-between"><span className="text-warmgrey">Shipping</span><span className="num text-charcoal">{data.shipping_usd != null ? money(data.shipping_usd) : "—"}</span></div>
        <div className="flex justify-between" data-testid="proposal-doc-tax">
          <span className="text-warmgrey">{tax?.label || "Sales Tax"}</span>
          <span className="num text-charcoal">{taxResolved ? money(tax.amount_usd) : <span className="text-[#8a6435]">Pending</span>}</span>
        </div>
        {taxResolved && (tax.breakdown || []).filter((r) => r.taxability_reason === "standard_rated").length > 1 &&
          tax.breakdown.filter((r) => r.taxability_reason === "standard_rated").map((r, i) => (
            <div key={i} className="flex justify-between pl-3 text-xs text-warmgrey" data-testid={`proposal-doc-tax-row-${i}`}><span>{r.label}</span><span className="num">{money(r.amount_usd)}</span></div>
          ))}
        <div className="flex justify-between border-t border-charcoal pt-2">
          <span className="font-medium text-charcoal">Total</span>
          <span className="num text-lg font-semibold text-charcoal" data-testid="proposal-doc-total">{data.total_usd != null ? money(data.total_usd) : "Pending tax"}</span>
        </div>
      </section>

      {(data.availability_note || data.terms) && (
        <section className="mt-8 space-y-4 border-t border-border pt-6 text-sm" data-testid="proposal-doc-terms">
          {data.availability_note && (<div><p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B86F54]">Availability</p><p className="mt-1 text-charcoal">{data.availability_note}</p></div>)}
          {data.terms && (<div><p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B86F54]">Payment terms</p><p className="mt-1 whitespace-pre-wrap text-charcoal">{data.terms}</p></div>)}
        </section>
      )}
      <footer className="mt-8 border-t border-border pt-4 text-center text-xs text-muted-foreground">
        Questions about this proposal? Contact {brand}{b.cs_email ? ` at ${b.cs_email}` : ""}.
      </footer>
    </article>
  );
}
